import { Suspense, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import moment from "moment-timezone";

import AppSidebar from "@shared/AppSidebar";
import LoadingIndicator from "@shared/LoadingIndicator";
import useGetData from "@/services/useGetData";
import { API_URLS } from "@/api/urls";
import useScreenBreakpoint from "@/hooks/useScreenBreakPoint";
import WarningScreen from "@shared/WarningScreen";
import useTabStore from "@/zustand/useTabStore";
import { useLogout } from "@/hooks/useAuth";
// import ReleasingNotesPopup from "@/components/ReleasingNotesPopup/ReleasingNotesPopup";
import PermissionProvider from "@/contexts/PermissionContext";
import type { PermissionsResponse } from "@/types/permissions";
import { TeeAgent } from "@/components/TeeAgent";
import { useTeeAgentStore } from "@/zustand/useTeeAgentStore";

interface IProps {
  children: React.ReactNode;
}

const PrivateLayout: React.FC<IProps> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSelectedTab } = useTabStore();
  const { setCount } = useTabStore();
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isBelowBreakpoint = useScreenBreakpoint(1023);
  const token = localStorage.getItem("accessToken");

  const { isOpen: isTeeAgentOpen, isPinned: isTeeAgentPinned } = useTeeAgentStore();
  const isSidebarPinned = isTeeAgentOpen && isTeeAgentPinned;

  // CRITICAL FIX: Use useQuery instead of getQueryData to make this REACTIVE
  // queryClient.getQueryData() does NOT trigger re-renders when cache updates
  // useQuery DOES subscribe to cache changes and triggers re-renders
  const { data: appData } = useQuery<{
    profile: Customer;
    permissions: PermissionsResponse;
    bookingSettings: UserBookingAllData;
    regionalSettings: RegionalSettings[];
  }>({
    queryKey: ["app-initial-data"],
    queryFn: () => Promise.resolve(null as never), // Never called — satisfies TanStack Query
    enabled: false, // Don't fetch - just read from cache
    staleTime: Infinity, // Data never goes stale
  });

  const { data: notificationData } = useGetData<INotificationResponse>(
    ["notification_list"],
    `${API_URLS.NOTIFICATIONS.GET_LATEST_NOTIFICATIONS}`
  );

  // Use cached data from AppDataLoader
  const permissionsResponse = appData?.permissions;
  const regionalSettings = appData?.regionalSettings;

  // Debug: Log permission state
  useEffect(() => {
    console.log("🔍 PrivateLayout - appData:", !!appData);
    console.log("🔍 PrivateLayout - permissionsResponse:", !!permissionsResponse);
    console.log("🔍 PrivateLayout - permissions count:", permissionsResponse?.permissions?.length);
    if (permissionsResponse) {
      console.log("✅ PrivateLayout - Permissions ARE available!");
    } else {
      console.warn("⚠️ PrivateLayout - Permissions NOT available yet!");
    }
  }, [appData, permissionsResponse]);

  const { mutate: logout } = useLogout();

  const handleLogout = useCallback(() => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      logout(
        { refresh: refreshToken },
        {
          onSuccess: (response) => {
            if (response) {
              // Clear all React Query cache to ensure fresh data for next user
              queryClient.clear();
              queryClient.removeQueries();

              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              navigate("/login");
            }
          },
          onError: (error) => {
            console.error("Logout Error:", error);
          },
        }
      );
    }
  }, [logout, navigate, queryClient]);

  // Set timezone from cached settings
  useEffect(() => {
    const timezoneFlag = Boolean(
      import.meta.env.VITE_TIMEZONE_FLAG?.toLowerCase() === "true"
    );
    if (regionalSettings?.[0]?.timezone && timezoneFlag) {
      moment.tz.setDefault(regionalSettings[0].timezone);
    }
  }, [regionalSettings]);

  useEffect(() => {
    if (location.pathname === "/bookings") {
      setSelectedTab("list");
    } else if (location.pathname === "/booking/calendar") {
      setSelectedTab("calendar");
    } else if (location.pathname === "/events/calendar") {
      setSelectedTab("calendar");
    } else if (location.pathname === "/league-events") {
      setSelectedTab("list");
    }
  }, [location.pathname, setSelectedTab]);

  useEffect(() => {
    if (notificationData) {
      setCount(notificationData.count);
    }
  }, [notificationData, setCount]);

  useEffect(() => {
    if (!token) {
      // Check if this is an invitation link that was incorrectly configured
      // (e.g., pointing to /dashboard/ instead of /user-invite/)
      const searchParams = new URLSearchParams(location.search);
      const acceptParam = searchParams.get("accept");
      const tokenParam = searchParams.get("token");
      const emailParam = searchParams.get("email");

      if (acceptParam === "True" && tokenParam) {
        // Redirect to the correct user invite page with the original params
        const inviteUrl = `/user-invite/?accept=True&email=${encodeURIComponent(emailParam || "")}&token=${tokenParam}`;
        navigate(inviteUrl, { replace: true });
        return;
      }

      navigate("/login");
    }
  }, [navigate, token, location.search]);

  useEffect(() => {
    const handleWindowEvents = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        handleLogout();
      }, 28800000);
    };

    // Listen for specific window events to reset the timer
    window.addEventListener("mousemove", handleWindowEvents);
    window.addEventListener("keydown", handleWindowEvents);
    window.addEventListener("click", handleWindowEvents);
    window.addEventListener("scroll", handleWindowEvents);

    handleWindowEvents();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener("mousemove", handleWindowEvents);
      window.removeEventListener("keydown", handleWindowEvents);
      window.removeEventListener("click", handleWindowEvents);
      window.removeEventListener("scroll", handleWindowEvents);
    };
  }, [navigate, location.pathname, handleLogout]);

  if (isBelowBreakpoint) {
    return <WarningScreen />;
  }

  // Check if current page is analytics page (but exclude Reports Schedule)
  const isAnalyticsPage = location.pathname.startsWith('/analytics') &&
    !location.pathname.includes('/analytics/reports_schedule');

  return (
    <>
      <PermissionProvider permissionsResponse={permissionsResponse}>
        <main className="flex min-h-screen">
          <AppSidebar />
          <div className={`flex-grow flex flex-col min-h-0 h-screen bg-gray1 pt-[10px] pb-3 pr-3 lg:pt-2.5 lg:pb-2 lg:pr-2 z-30 transition-all duration-300 ${isSidebarPinned ? 'mr-0 pr-0 lg:pr-0' : ''}`}>
            <div className={`${isAnalyticsPage ? 'bg-[#F5F8FF]' : 'bg-wht'} flex-1 min-h-0 overflow-y-auto overflow-x-hidden rounded-3xl custom-scroll ${isSidebarPinned ? 'rounded-r-none' : ''}`}>
              {/* <ReleasingNotesPopup /> */}
              <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
            </div>
          </div>
          {/* Tee Agent - AI Co-pilot */}
          <TeeAgent />
        </main>
      </PermissionProvider>
    </>
  );
};

export default PrivateLayout;
